import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Badge, Tabs, Tab, Table } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import PlayerList, { PlayerListItem } from '../PlayerList';
import { gameService } from '../../services/gameService';
import { GameSpaceInfo, GameSession, HostInfo } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { useGame } from '../../contexts/GameContext';
import { useGameSpace } from '../../contexts/GameSpaceContext';

const GameSpaceLobby: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const [gameSpace, setGameSpace] = useState<GameSpaceInfo | null>(null);
  const [activeGames, setActiveGames] = useState<GameSession[]>([]);
  const [pastGames, setPastGames] = useState<GameSession[]>([]);
  const [hostInfo, setHostInfo] = useState<HostInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [joiningGame, setJoiningGame] = useState<boolean>(false);
  const { user } = useAuth();
  const { joinGame } = useGame();
  const { gameSpaces, loading: gameSpacesLoading } = useGameSpace();
  const navigate = useNavigate();

  useEffect(() => {
    if (!id) {
      // If no ID is provided, use the game spaces from context and redirect to the first one
      if (!gameSpacesLoading && gameSpaces.length > 0) {
        // Navigate to the first game space
        navigate(`/gamespace/${gameSpaces[0].id}`, { replace: true });
      } else if (!gameSpacesLoading) {
        // If no game spaces are found, redirect to the game spaces list
        navigate('/gamespaces');
      }
      return;
    }
    
    // If ID is provided, fetch the specific game space data
    const fetchGameSpaceData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const gameSpaceId = parseInt(id);
        const [spaceResponse, activeResponse, pastResponse, hostResponse] = await Promise.all([
          gameService.getGameSpace(gameSpaceId),
          gameService.getActiveGamesInSpace(gameSpaceId),
          gameService.getPastGamesInSpace(gameSpaceId),
          gameService.getHostInfo()
        ]);
        
        if (spaceResponse.success) setGameSpace(spaceResponse.data);
        else {
          setError(spaceResponse.message || 'Failed to load GameSpace details');
          setLoading(false);
          return;
        }
        
        if (activeResponse.success) setActiveGames(activeResponse.data);
        if (pastResponse.success) setPastGames(pastResponse.data);
        if (hostResponse.success) setHostInfo(hostResponse.data);
      } catch (err) {
        console.error('Error fetching GameSpace data:', err);
        setError('An unexpected error occurred while loading GameSpace data');
      } finally {
        setLoading(false);
      }
    };

    fetchGameSpaceData();
  }, [id, user, navigate]);

  const handleJoinGame = async (gameCode: string) => {
    try {
      setJoiningGame(true);
      const success = await joinGame(gameCode);
      if (success) {
        navigate('/lobby');
      } else {
        setError('Failed to join game');
      }
    } catch (err) {
      console.error('Error joining game:', err);
      setError('An unexpected error occurred while joining the game');
    } finally {
      setJoiningGame(false);
    }
  };

  const handleCreateGame = () => {
    if (gameSpace) {
      navigate(`/create?gameSpaceId=${gameSpace.id}`);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading GameSpace...</p>
      </Container>
    );
  }

  if (error || !gameSpace) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Error</Alert.Heading>
          <p>{error || 'GameSpace not found'}</p>
          <hr />
          <div className="d-flex justify-content-end">
            <Button 
              variant="outline-danger" 
              onClick={() => navigate(user?.userType === 'Member' ? '/gamespaces' : '/')}
            >
              {user?.userType === 'Member' ? 'Back to GameSpaces' : 'Back to Home'}
            </Button>
          </div>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          {user?.userType === 'Member' && (
            <Button 
              variant="outline-secondary" 
              size="sm" 
              className="mb-3"
              onClick={() => navigate('/gamespaces')}
            >
              <i className="fas fa-arrow-left me-2"></i>
              Back to GameSpaces
            </Button>
          )}
          <h1 className="display-5 fw-bold">
            <i className="fas fa-dice me-3 text-primary"></i>
            {gameSpace.title}
          </h1>
          <p className="lead">
            {gameSpace.description || 'Welcome to your GameSpace!'}
          </p>
          <div className="d-flex flex-wrap gap-2 mb-3">
            <Badge bg={gameSpace.isPublic ? "success" : "secondary"}>
              {gameSpace.isPublic ? "Public" : "Private"}
            </Badge>
            <Badge bg={gameSpace.allowExternalHost ? "info" : "warning"}>
              {gameSpace.allowExternalHost ? "External Hosting" : "Owner Hosting Only"}
            </Badge>
            <Badge bg="dark">
              <i className="fas fa-users me-1"></i> Max {gameSpace.maxPlayers} players
            </Badge>
            <Badge bg="primary">
              <i className="fas fa-gamepad me-1"></i> {gameSpace.gameCount} games
            </Badge>
          </div>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col className="d-flex justify-content-end">
          <Button 
            variant="primary" 
            size="lg"
            onClick={handleCreateGame}
            disabled={!hostInfo || (user?.userType === 'Player' && (hostInfo?.tokenBalance || 0) < 1)}
          >
            <i className="fas fa-plus-circle me-2"></i>
            Create New Game
          </Button>
        </Col>
      </Row>

      <Tabs defaultActiveKey="active" className="mb-4">
        <Tab eventKey="active" title="Active Games">
          {activeGames.length === 0 ? (
            <Alert variant="info" className="mt-3">
              <p className="mb-0">No active games in this GameSpace. Create a new game to get started!</p>
            </Alert>
          ) : (
            <div className="mt-3">
              {activeGames.map((game) => (
                <Card key={game.id} className="mb-3 border-0 shadow-sm">
                  <Card.Header className="bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <Badge bg="primary" className="me-2">{game.gameCode}</Badge>
                        <span className="fw-bold">{game.title}</span>
                        <Badge bg={game.statusName === 'WaitingForPlayers' ? 'warning' : 'success'} className="ms-2">
                          {game.statusName === 'WaitingForPlayers' ? 'Waiting' : 'In Progress'}
                        </Badge>
                      </div>
                      <div>
                        <small className="text-muted me-3">
                          Started: {new Date(game.startTime).toLocaleTimeString()}
                        </small>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => handleJoinGame(game.gameCode)}
                          disabled={joiningGame}
                        >
                          {joiningGame ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-1" />
                              Joining...
                            </>
                          ) : 'Join'}
                        </Button>
                      </div>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col>
                        <PlayerList 
                          title={`Players (${game.currentPlayerCount}/${game.maxPlayers})`}
                          players={game.players?.map(player => ({
                            id: player.id,
                            userId: player.userId.toString(),
                            displayName: player.displayName || 'Unknown',
                            playerName: player.displayName || 'Unknown Player',
                            isConnected: player.isConnected || false,
                            isHost: player.isHost || false,
                          })) || []}
                          gameCode={game.gameCode}
                          gameId={game.id}
                          isHost={false}
                          refreshPlayers={async () => { return true; }}
                          initiallyCollapsed={true}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              ))}
            </div>
          )}
        </Tab>
        <Tab eventKey="past" title="Past Games">
          {pastGames.length === 0 ? (
            <Alert variant="info" className="mt-3">
              <p className="mb-0">No past games found in this GameSpace.</p>
            </Alert>
          ) : (
            <Table responsive hover className="mt-3">
              <thead>
                <tr>
                  <th>Game Code</th>
                  <th>Title</th>
                  <th>Players</th>
                  <th>Started</th>
                  <th>Ended</th>
                </tr>
              </thead>
              <tbody>
                {pastGames.map((game) => (
                  <tr key={game.id}>
                    <td>
                      <Badge bg="secondary">{game.gameCode}</Badge>
                    </td>
                    <td>{game.title}</td>
                    <td>{game.currentPlayerCount} / {game.maxPlayers}</td>
                    <td>{new Date(game.startTime).toLocaleTimeString()} {new Date(game.startTime).toLocaleDateString()}</td>
                    <td>{game.endTime ? `${new Date(game.endTime).toLocaleTimeString()} ${new Date(game.endTime).toLocaleDateString()}` : 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Tab>
        <Tab eventKey="settings" title="Settings">
          <Card className="mt-3">
            <Card.Body>
              <Card.Title>GameSpace Settings</Card.Title>
              <Card.Text>
                Manage your GameSpace settings and configurations.
              </Card.Text>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Created:</strong> {new Date(gameSpace.createdAt).toLocaleDateString()}
                </Col>
                <Col md={6}>
                  <strong>Status:</strong> {gameSpace.isActive ? 'Active' : 'Inactive'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Visibility:</strong> {gameSpace.isPublic ? 'Public' : 'Private'}
                </Col>
                <Col md={6}>
                  <strong>External Hosting:</strong> {gameSpace.allowExternalHost ? 'Allowed' : 'Not Allowed'}
                </Col>
              </Row>
              {(user?.userType === 'Member' || user?.userType === 'Player') && (
                <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                  <Button variant="outline-primary">
                    <i className="fas fa-cog me-2"></i>
                    Edit Settings
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </Container>
  );
};

export default GameSpaceLobby;
